import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    TextField,
    CircularProgress,
    Divider,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { showToast } from '@klpkonsern/ui';

import { Block } from '@klp/design';
import {
    useGetBrukereByggListQuery,
    useGetByggProsjektkontrollerListQuery,
} from '../services/rtkApi';
import type { Bruker } from '../types';

interface ByggProsjektkontrollerProps {
    isEnabled: boolean;
    prosjektkontrollerUser: Bruker;
    hasEditAccess: boolean;
    callbackChange: (
        selectedByggAndFirma: Array<string>,
        selectedAssignedByggAndFirma: Array<string>,
    ) => void;
}

export const ByggProsjektkontroller = ({
    isEnabled,
    prosjektkontrollerUser,
    hasEditAccess,
    callbackChange,
}: ByggProsjektkontrollerProps) => {
    const { t } = useTranslation();
    const {
        data: byggListData = [],
        isError: byggListError,
        isLoading: byggListIsLoading,
        isFetching: byggListIsFetching,
    } = useGetBrukereByggListQuery({});
    const {
        data: byggProsjektkontrollerList = [],
        isError: byggProsjektkontrollerListError,
        isLoading: byggProsjektkontrollerListLoading,
        isFetching: byggProsjektkontrollerListFetching,
    } = useGetByggProsjektkontrollerListQuery();

    const [selectedBygg, setSelectedBygg] = useState<Array<string>>(
        prosjektkontrollerUser?.byggProsjektkontroller
            ?.filter((bpk) => bpk.byggNummer && bpk.byggNummer.length === 6)
            .map((bpk) => bpk.byggNummer!) ?? [],
    );
    const [selectedAssignedBygg, setSelectedAssignedBygg] = useState<
        Array<string>
    >([]);
    const [selectedFirma, setSelectedFirma] = useState<Array<string>>(
        prosjektkontrollerUser?.byggProsjektkontroller
            ?.filter((bpk) => bpk.byggNummer && bpk.byggNummer.length === 4)
            .map((bpk) => bpk.byggNummer!) ?? [],
    );
    const [selectedAssignedFirma, setSelectedAssignedFirma] = useState<
        Array<string>
    >([]);

    const assignedByggProsjektkontroller = useMemo(
        () =>
            Object.fromEntries(
                byggProsjektkontrollerList
                    .filter(
                        (bpk) =>
                            bpk.brukerId !== prosjektkontrollerUser.brukerId,
                    )
                    .map((bpk) => [bpk.byggNummer, bpk.brukerId]),
            ),
        [byggProsjektkontrollerList, prosjektkontrollerUser],
    );

    const filteredBygg = useMemo(
        () =>
            byggListData
                ?.filter((b) => b.nummer?.length === 6 && b.navn)
                .map((b) => {
                    const assignedByggProsjektkontrollerSearch =
                        assignedByggProsjektkontroller[b.nummer!];
                    return assignedByggProsjektkontrollerSearch
                        ? `${b.navn!} (${t(
                              'users.bpkModal.assigned',
                          )} ${assignedByggProsjektkontrollerSearch})`
                        : b.navn!;
                }),
        [byggListData, assignedByggProsjektkontroller],
    );

    const filteredFirma = useMemo(() => {
        if (!byggListData) return [];

        // Extract unique firmaIds using a Set
        const uniqueFirma = Array.from(
            new Set(
                byggListData.reduce<Array<string>>((acc, { firmaId }) => {
                    if (firmaId) acc.push(firmaId);
                    return acc;
                }, []),
            ),
        );

        return uniqueFirma.map((firmaId) => {
            const assignedByggProsjektkontrollerSearch =
                assignedByggProsjektkontroller[firmaId];
            return assignedByggProsjektkontrollerSearch
                ? `${firmaId} (${t('users.bpkModal.assigned')} ${assignedByggProsjektkontrollerSearch})`
                : firmaId;
        });
    }, [byggListData, assignedByggProsjektkontroller]);

    useEffect(() => {
        if (!byggListError && !byggProsjektkontrollerListError) return;

        showToast({ body: t('general.error.message'), type: 'error' });
    }, [byggListError, byggProsjektkontrollerListError]);

    useEffect(() => {
        const selectedByggAndFirma = selectedBygg.concat(selectedFirma);
        const selectedAssignedByggAndFirma = selectedAssignedBygg.concat(
            selectedAssignedFirma,
        );

        callbackChange(selectedByggAndFirma, selectedAssignedByggAndFirma);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedBygg,
        selectedFirma,
        selectedAssignedFirma,
        selectedAssignedBygg,
    ]);

    const selectFieldIsLoading =
        byggListIsLoading ||
        byggListIsFetching ||
        byggProsjektkontrollerListLoading ||
        byggProsjektkontrollerListFetching;

    if (!isEnabled) return null;

    return (
        <Block paddingTop="massive">
            <Divider />
            <Block paddingTop="compact">
                {hasEditAccess ? (
                    <Typography>{t('users.bpkModal.description')}</Typography>
                ) : (
                    <Typography>
                        {t('users.showUserInfo.prosjektkontrollerDescription')}
                    </Typography>
                )}
            </Block>
            <Block paddingTop="compact">
                <Autocomplete
                    disabled={!hasEditAccess}
                    multiple={true}
                    disableCloseOnSelect={true}
                    id="firmaSelector"
                    options={filteredFirma || []}
                    getOptionSelected={(option, value) =>
                        option.startsWith(value)
                    }
                    value={selectedFirma}
                    onChange={(_, values) => {
                        const onlyIdMappedValues = values.map((v) => {
                            const match = v.match(/^\S+/);
                            return match ? match[0] : v;
                        });
                        const assignedValues = onlyIdMappedValues.filter(
                            (v) => assignedByggProsjektkontroller[v],
                        );
                        setSelectedFirma(onlyIdMappedValues);
                        setSelectedAssignedFirma(assignedValues);
                    }}
                    loading={selectFieldIsLoading}
                    ChipProps={{
                        color: 'secondary',
                        variant: 'outlined',
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={
                                hasEditAccess
                                    ? t('users.bpkModal.labelFirma')
                                    : t('users.showUserInfo.labelFirma')
                            }
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {selectFieldIsLoading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={30}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                {selectedAssignedFirma.length > 0 && (
                    <Block paddingTop="tiny" paddingBottom="tiny">
                        <Alert severity="warning">
                            <Typography>
                                {t('users.bpkModal.assignedFirmaWarning', {
                                    name: prosjektkontrollerUser?.fulltnavn,
                                })}
                            </Typography>
                            <ul>
                                {selectedAssignedFirma.map((v) => (
                                    <li
                                        key={v}
                                    >{`${v} - ${assignedByggProsjektkontroller[v]}`}</li>
                                ))}
                            </ul>
                        </Alert>
                    </Block>
                )}
            </Block>
            <Block paddingTop="compact">
                <Autocomplete
                    disabled={!hasEditAccess}
                    multiple={true}
                    disableCloseOnSelect={true}
                    id="byggSelector"
                    options={filteredBygg || []}
                    value={selectedBygg}
                    getOptionSelected={(option, value) =>
                        option.startsWith(value)
                    }
                    onChange={(_, values) => {
                        const onlyIdMappedValues = values.map((v) => {
                            const match = v.match(/^\S+/);
                            return match ? match[0] : v;
                        });
                        const assignedValues = onlyIdMappedValues.filter(
                            (v) => assignedByggProsjektkontroller[v],
                        );
                        setSelectedBygg(onlyIdMappedValues);
                        setSelectedAssignedBygg(assignedValues);
                    }}
                    loading={selectFieldIsLoading}
                    ChipProps={{
                        color: 'secondary',
                        variant: 'outlined',
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={
                                hasEditAccess
                                    ? t('users.bpkModal.labelBygg')
                                    : t('users.showUserInfo.labelBygg')
                            }
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {selectFieldIsLoading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={30}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                {selectedAssignedBygg.length > 0 && (
                    <Block paddingTop="tiny" paddingBottom="tiny">
                        <Alert severity="warning">
                            <Typography>
                                {t('users.bpkModal.assignedByggWarning', {
                                    name: prosjektkontrollerUser?.fulltnavn,
                                })}
                            </Typography>
                            <ul>
                                {selectedAssignedBygg.map((v) => (
                                    <li
                                        key={v}
                                    >{`${v} - ${assignedByggProsjektkontroller[v]}`}</li>
                                ))}
                            </ul>
                        </Alert>
                    </Block>
                )}
            </Block>
        </Block>
    );
};
